/** Attendance filter type options — value sent to API; label shown in UI. */
export const ATTENDANCE_TYPE_FILTER_OPTIONS = [
  { label: 'Sick Leave', value: 'sick_leave' },
  { label: 'Medical Leave', value: 'medical_leave' },
  { label: 'Vacation', value: 'vacation' },
  { label: 'Remote Work', value: 'remote_work' },
  { label: 'Absent', value: 'absent' },
  { label: 'Late Arrival', value: 'late_arrival' },
  { label: 'Personal Leave', value: 'personal_leave' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'LOA', value: 'LOA' }
];

export const TIME_OFF_TYPE_OPTIONS = [
  { label: 'Sick Leave', value: 'sick_leave' },
  { label: 'Medical Leave', value: 'medical_leave' },
  { label: 'Vacation', value: 'vacation' },
  { label: 'Remote Work', value: 'remote_work' },
  { label: 'Absent', value: 'absent' },
  { label: 'Late Arrival', value: 'late_arrival' },
  { label: 'Personal Leave', value: 'personal_leave' }
];

/** Direct types dept_admin may create without eLeave (matches server allow-list). */
export const TIME_OFF_DEPT_ADMIN_DIRECT_TYPES = ['sick_leave', 'absent', 'late_arrival'];

export const TIME_OFF_TYPE_LABELS = Object.fromEntries(
  TIME_OFF_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function getDirectTimeOffTypeOptions(canCreateAllTypes) {
  if (canCreateAllTypes) return TIME_OFF_TYPE_OPTIONS;
  return TIME_OFF_TYPE_OPTIONS.filter((o) => TIME_OFF_DEPT_ADMIN_DIRECT_TYPES.includes(o.value));
}

export const ATTENDANCE_STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Rehired', value: 'Rehired' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'LOA', value: 'LOA' }
];

export const ATTENDANCE_COLUMN_TYPES = {
  sick: 'sick_leave',
  medical: 'medical_leave',
  vacation: 'vacation',
  remote: 'remote_work'
};

export const TIME_OFF_TYPE_COLORS = {
  sick_leave: '#ef4444',
  medical_leave: '#be185d',
  vacation: '#16a34a',
  personal_leave: '#16a34a',
  remote_work: '#3b82f6',
  absent: '#a855f7',
  late_arrival: '#a855f7'
};

export const ATTENDANCE_TABLE_COLUMNS = [
  {
    key: 'sick',
    sortField: 'totals.sick',
    header: 'Sick',
    color: TIME_OFF_TYPE_COLORS.sick_leave,
    type: ATTENDANCE_COLUMN_TYPES.sick
  },
  {
    key: 'medical',
    sortField: 'totals.medical',
    header: 'Medical',
    color: TIME_OFF_TYPE_COLORS.medical_leave,
    type: ATTENDANCE_COLUMN_TYPES.medical
  },
  {
    key: 'vacation',
    sortField: 'totals.vacation',
    header: 'Vacation',
    color: TIME_OFF_TYPE_COLORS.vacation,
    type: ATTENDANCE_COLUMN_TYPES.vacation
  },
  {
    key: 'remote',
    sortField: 'totals.remote',
    header: 'Remote',
    color: TIME_OFF_TYPE_COLORS.remote_work,
    type: ATTENDANCE_COLUMN_TYPES.remote
  }
];

/** Map legacy attendance table sort keys saved before totals.* paths. */
export const ATTENDANCE_SORT_FIELD_ALIASES = Object.fromEntries(
  ATTENDANCE_TABLE_COLUMNS.map((col) => [col.key, col.sortField])
);

/** Mock default until allowance fields exist on employees (Phase 5+). */
export const DEFAULT_VACATION_ALLOWANCE = 28;

export function getTimeOffTypeColor(type) {
  return TIME_OFF_TYPE_COLORS[type] || '#a855f7';
}

export function getTimeOffTypeLabel(type) {
  return TIME_OFF_TYPE_LABELS[type] || type;
}

/** Leave types for the "Leave" history filter (excludes late_arrival → issues). */
export const LEAVE_ENTRY_TYPES = [
  'sick_leave',
  'medical_leave',
  'vacation',
  'personal_leave',
  'absent'
];

/** Types shown under the "Attendance issues" history filter. */
export const ATTENDANCE_ISSUE_TYPES = ['late_arrival', 'absent'];

/** Scheduled absences for the status-strip "next absence" widget (excludes late). */
export const SCHEDULED_ABSENCE_TYPES = [
  'vacation',
  'sick_leave',
  'medical_leave',
  'personal_leave',
  'absent'
];

export function yearDateRange(year) {
  const y = Number(year) || new Date().getFullYear();
  return {
    date_from: `${y}-01-01`,
    date_to: `${y}-12-31`
  };
}

export function currentYearDateRange() {
  return yearDateRange(new Date().getFullYear());
}

export function formatAttendanceDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export { toApiDate } from '@/utils/dateHelper';

export function isValidDateRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return true;
  return dateFrom <= dateTo;
}

/** Map attendance/get summary to workspace header StatCards. */
export function mapAttendanceHeaderStats(summary) {
  const s = summary || {};
  return [
    { key: 'sick', label: 'Sick', value: formatAttendanceDays(s.sick?.days), tone: 'sick' },
    { key: 'vacation', label: 'Vacation', value: formatAttendanceDays(s.vacation?.days), tone: 'vacation' },
    { key: 'remote', label: 'Remote', value: formatAttendanceDays(s.remote?.days), tone: 'remote' },
    { key: 'medical', label: 'Medical', value: formatAttendanceDays(s.medical?.days), tone: 'medical' },
    { key: 'absent', label: 'Absent', value: formatAttendanceDays(s.absent?.days), tone: 'absent' },
    { key: 'late', label: 'Late', value: String(s.late?.employees ?? 0), tone: 'late' }
  ];
}
